/**
 * useTranslation Hook
 *
 * Hook for accessing UI translations based on user's language preference
 */

import { useCallback } from 'react';
import { useAuthStore } from '@/stores/authStore';
import {
  getTranslation,
  getTranslationWithParams,
  type TranslationKey,
} from '@/locales';
import { DEFAULT_LANGUAGE } from '@/lib/utils/constants';
import type { LanguageCode } from '@/types/user';

export interface UseTranslationReturn {
  /** Get translation for a key */
  t: (key: TranslationKey, params?: Record<string, string>) => string;
  /** Current language */
  language: LanguageCode;
}

/**
 * Hook for accessing UI translations
 * Uses the authenticated user's language preference
 */
export function useTranslation(): UseTranslationReturn {
  const user = useAuthStore((state) => state.user);
  const language: LanguageCode = user?.language ?? DEFAULT_LANGUAGE;

  const t = useCallback(
    (key: TranslationKey, params?: Record<string, string>): string => {
      if (params) {
        return getTranslationWithParams(key, params, language);
      }
      return getTranslation(key, language);
    },
    [language]
  );

  return {
    t,
    language,
  };
}

export default useTranslation;
